<script>
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useSecurityStore} from "@/stores/Security";
import axios from "axios";
import {ref, watch} from "vue";
import {useEvaluationFrameworkStore} from "@/stores/Evaluation/EvaluationFramework";

export default {
    setup() {
        const {
            total,
            loading,
            filter,
        } = useSharedData();

        const {transformFilterValues} = useSharedMethod();

        const {$i18n, $router} = useTools();

        const search = ref(null);

        const evaluationFrameworkStore = useEvaluationFrameworkStore();
        const securityStore = useSecurityStore();

        const headers = [
            {
                text: "...",
                align: "left",
                sortable: true,
                value: "action",
                width: "0%"
            },
            {
                text: $i18n.t('common.code'),
                align: "left",
                sortable: true,
                value: "code",
                width: "0%"
            },
            {
                text: $i18n.t('module.occupation'),
                align: "left",
                sortable: true,
                value: "occupationName",
                width: "0%"
            },
            {
                text: $i18n.t('common.comment'),
                align: "left",
                sortable: true,
                value: "notes"
            },
        ];

        const actions = [
            {
                title: $i18n.t("common.edit"),
                icon: 'mdi-pencil-box-multiple',
                color: 'orange',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'evaluation_skill_frameworks.edit', params: {id: item.id}})
                },
            },
            {
                title: $i18n.t("common.delete"),
                icon: 'mdi-trash-can',
                color: 'red',
                access: () => true,
                disabled: () => false,
                func: (item) => {
                    $router.push({name: 'evaluation_skill_frameworks.delete', params: {id: item.id}})
                },
            },
        ];

        function findAll() {
            loading.value = true;
            axios.get('/evaluationSkillFrameworks', {params: transformFilterValues(filter.value)}).then((response) => {
                total.value = response.data.length;
                evaluationFrameworkStore.setEvaluationFrameworks(response.data);
            }).finally(() => {
                loading.value = false;
            });
        }

        watch(() => filter.value, () => {
            findAll();
        }, {deep: true, immediate: true});

        return {
            evaluationFrameworkStore,
            securityStore,
            headers,
            actions,
            total,
            loading,
            filter,
            search,
            findAll,
        };
    },
}
</script>

<template>
    <div>
        <w-app
            :title="$t('module.evaluation_skill_framework')"
            show-total
            :total="total"
        >
            <template v-slot:rightToolbar>
                <w-btn-reload
                    :loading="loading"
                    @press="findAll"
                />
                <w-btn-add
                    :show="true"
                    @press="$router.push({ name: 'evaluation_skill_frameworks.add' })"
                />
            </template>
            <template v-slot:content>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="3">
                            <w-text-field
                                v-model="search"
                                :label="$t('common.search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
                <w-data-table
                    :headers="headers"
                    :actions="actions"
                    :search="search"
                    :items="evaluationFrameworkStore.evaluationFrameworks"
                    :total="total"
                    :loading="loading"
                    sort-by="occupationName"
                    show-expand
                    single-expand
                >
                    <template v-slot:[`item.code`]="{ item }">
                        <w-chip :text="item.code"/>
                    </template>
                    <template v-slot:[`item.occupationName`]="{ item }">
                        <w-inline-text bold :text="item.occupationName"/>
                    </template>
                    <template v-slot:expanded-item="{ item, headers }">
                        <td :colspan="2" style="background-color: #EEEEEE"></td>
                        <td :colspan="headers.length - 2" style="background-color: #EEEEEE">
                            <v-container fluid>
                                <v-simple-table dense class="elevation-5">
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left">{{ $t('common.skill') }}</th>
                                            <th class="text-left">{{ $t('evaluation.level') }}</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                            v-for="(requirement, index) in item.requirements"
                                            :key="`requirement-${index}-${requirement.id}`"
                                        >
                                            <td class="text-left">
                                                {{ requirement.catalogName }}
                                            </td>
                                            <td class="text-left">
                                                <w-chip outlined :text="requirement.gravityName"/>
                                                <w-btn-icon-help :text="requirement.notes"/>
                                            </td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </v-container>
                        </td>
                    </template>
                </w-data-table>
            </template>
        </w-app>
        <router-view/>
    </div>
</template>